/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { ClosedEnum } from "../../types/enums.js";

/**
 * Who the upstream access credential belongs to, fixed at creation. subject (the default) means each caller connects their own upstream account through the client. self means the client obtains a credential for itself with the client_credentials grant and every caller shares it; it requires token_endpoint_auth_method client_secret_basic, client_secret_post (both with client_secret) or private_key_jwt (with json_web_key_set_id), and an issuer with a token_endpoint.
 */
export const CreateOrganizationRemoteSessionClientFormCredentialOwner = {
  Subject: "subject",
  Self: "self",
} as const;
/**
 * Who the upstream access credential belongs to, fixed at creation. subject (the default) means each caller connects their own upstream account through the client. self means the client obtains a credential for itself with the client_credentials grant and every caller shares it; it requires token_endpoint_auth_method client_secret_basic, client_secret_post (both with client_secret) or private_key_jwt (with json_web_key_set_id), and an issuer with a token_endpoint.
 */
export type CreateOrganizationRemoteSessionClientFormCredentialOwner =
  ClosedEnum<typeof CreateOrganizationRemoteSessionClientFormCredentialOwner>;

/**
 * Identifier used as the aud claim in private_key_jwt assertions. Omit to use the issuer identifier; token_endpoint is available for providers that require the token endpoint URL.
 */
export const CreateOrganizationRemoteSessionClientFormTokenEndpointAuthAudienceFormat =
  {
    Issuer: "issuer",
    TokenEndpoint: "token_endpoint",
  } as const;
/**
 * Identifier used as the aud claim in private_key_jwt assertions. Omit to use the issuer identifier; token_endpoint is available for providers that require the token endpoint URL.
 */
export type CreateOrganizationRemoteSessionClientFormTokenEndpointAuthAudienceFormat =
  ClosedEnum<
    typeof CreateOrganizationRemoteSessionClientFormTokenEndpointAuthAudienceFormat
  >;

/**
 * How the client authenticates at the issuer's token endpoint. Omit to default to client_secret_basic.
 */
export const CreateOrganizationRemoteSessionClientFormTokenEndpointAuthMethod =
  {
    ClientSecretBasic: "client_secret_basic",
    ClientSecretPost: "client_secret_post",
    None: "none",
    PrivateKeyJwt: "private_key_jwt",
  } as const;
/**
 * How the client authenticates at the issuer's token endpoint. Omit to default to client_secret_basic.
 */
export type CreateOrganizationRemoteSessionClientFormTokenEndpointAuthMethod =
  ClosedEnum<
    typeof CreateOrganizationRemoteSessionClientFormTokenEndpointAuthMethod
  >;

/**
 * Form for an org admin to register a standalone remote_session_client under an existing issuer, with no user_session_issuer attachments.
 */
export type CreateOrganizationRemoteSessionClientForm = {
  /**
   * Optional upstream OAuth audience to send on the authorize redirect and token exchange.
   */
  audience?: string | undefined;
  /**
   * client_id supplied by the caller, e.g. from Dynamic Client Registration.
   */
  clientId: string;
  /**
   * When the issuer reported issuing the client_id (RFC 7591 client_id_issued_at). Omit to record the time of this call.
   */
  clientIdIssuedAt?: Date | undefined;
  /**
   * Optional client_secret supplied by the caller. Speakeasy encrypts before persisting; the plaintext is never returned.
   */
  clientSecret?: string | undefined;
  /**
   * When the issuer reported the client secret expires (RFC 7591 client_secret_expires_at). Omit when the issuer reported no expiry.
   */
  clientSecretExpiresAt?: Date | undefined;
  /**
   * Who the upstream access credential belongs to, fixed at creation. subject (the default) means each caller connects their own upstream account through the client. self means the client obtains a credential for itself with the client_credentials grant and every caller shares it; it requires token_endpoint_auth_method client_secret_basic, client_secret_post (both with client_secret) or private_key_jwt (with json_web_key_set_id), and an issuer with a token_endpoint.
   */
  credentialOwner?:
    | CreateOrganizationRemoteSessionClientFormCredentialOwner
    | undefined;
  /**
   * Organization JSON Web Key Set to sign private_key_jwt assertions with. Required when token_endpoint_auth_method is private_key_jwt and optional otherwise, as with attachKeySet. Must belong to the caller's organization, which needs the customer-managed encryption keys entitlement.
   */
  jsonWebKeySetId?: string | undefined;
  /**
   * Owning project id for the new client; the project must belong to the caller's organization. Omit to inherit a project-specific issuer's project, or to create an organization-level client (no project, attachable by every project) under an organization-level issuer.
   */
  projectId?: string | undefined;
  /**
   * The owning remote_session_issuer id; must belong to the caller's organization.
   */
  remoteSessionIssuerId: string;
  /**
   * Explicit upstream OAuth scopes the dance should request for this client. Omit to fall back to the issuer's scopes_supported.
   */
  scope?: Array<string> | undefined;
  /**
   * Identifier used as the aud claim in private_key_jwt assertions. Omit to use the issuer identifier; token_endpoint is available for providers that require the token endpoint URL.
   */
  tokenEndpointAuthAudienceFormat?:
    | CreateOrganizationRemoteSessionClientFormTokenEndpointAuthAudienceFormat
    | undefined;
  /**
   * How the client authenticates at the issuer's token endpoint. Omit to default to client_secret_basic.
   */
  tokenEndpointAuthMethod?:
    | CreateOrganizationRemoteSessionClientFormTokenEndpointAuthMethod
    | undefined;
};

/** @internal */
export const CreateOrganizationRemoteSessionClientFormCredentialOwner$outboundSchema:
  z.ZodMiniEnum<
    typeof CreateOrganizationRemoteSessionClientFormCredentialOwner
  > = z.enum(CreateOrganizationRemoteSessionClientFormCredentialOwner);

/** @internal */
export const CreateOrganizationRemoteSessionClientFormTokenEndpointAuthAudienceFormat$outboundSchema:
  z.ZodMiniEnum<
    typeof CreateOrganizationRemoteSessionClientFormTokenEndpointAuthAudienceFormat
  > = z.enum(
    CreateOrganizationRemoteSessionClientFormTokenEndpointAuthAudienceFormat,
  );

/** @internal */
export const CreateOrganizationRemoteSessionClientFormTokenEndpointAuthMethod$outboundSchema:
  z.ZodMiniEnum<
    typeof CreateOrganizationRemoteSessionClientFormTokenEndpointAuthMethod
  > = z.enum(CreateOrganizationRemoteSessionClientFormTokenEndpointAuthMethod);

/** @internal */
export type CreateOrganizationRemoteSessionClientForm$Outbound = {
  audience?: string | undefined;
  client_id: string;
  client_id_issued_at?: string | undefined;
  client_secret?: string | undefined;
  client_secret_expires_at?: string | undefined;
  credential_owner: string;
  json_web_key_set_id?: string | undefined;
  project_id?: string | undefined;
  remote_session_issuer_id: string;
  scope?: Array<string> | undefined;
  token_endpoint_auth_audience_format?: string | undefined;
  token_endpoint_auth_method?: string | undefined;
};

/** @internal */
export const CreateOrganizationRemoteSessionClientForm$outboundSchema:
  z.ZodMiniType<
    CreateOrganizationRemoteSessionClientForm$Outbound,
    CreateOrganizationRemoteSessionClientForm
  > = z.pipe(
    z.object({
      audience: z.optional(z.string()),
      clientId: z.string(),
      clientIdIssuedAt: z.optional(
        z.pipe(z.date(), z.transform(v => v.toISOString())),
      ),
      clientSecret: z.optional(z.string()),
      clientSecretExpiresAt: z.optional(
        z.pipe(z.date(), z.transform(v => v.toISOString())),
      ),
      credentialOwner: z._default(
        CreateOrganizationRemoteSessionClientFormCredentialOwner$outboundSchema,
        "subject",
      ),
      jsonWebKeySetId: z.optional(z.string()),
      projectId: z.optional(z.string()),
      remoteSessionIssuerId: z.string(),
      scope: z.optional(z.array(z.string())),
      tokenEndpointAuthAudienceFormat: z.optional(
        CreateOrganizationRemoteSessionClientFormTokenEndpointAuthAudienceFormat$outboundSchema,
      ),
      tokenEndpointAuthMethod: z.optional(
        CreateOrganizationRemoteSessionClientFormTokenEndpointAuthMethod$outboundSchema,
      ),
    }),
    z.transform((v) => {
      return remap$(v, {
        clientId: "client_id",
        clientIdIssuedAt: "client_id_issued_at",
        clientSecret: "client_secret",
        clientSecretExpiresAt: "client_secret_expires_at",
        credentialOwner: "credential_owner",
        jsonWebKeySetId: "json_web_key_set_id",
        projectId: "project_id",
        remoteSessionIssuerId: "remote_session_issuer_id",
        tokenEndpointAuthAudienceFormat: "token_endpoint_auth_audience_format",
        tokenEndpointAuthMethod: "token_endpoint_auth_method",
      });
    }),
  );

export function createOrganizationRemoteSessionClientFormToJSON(
  createOrganizationRemoteSessionClientForm:
    CreateOrganizationRemoteSessionClientForm,
): string {
  return JSON.stringify(
    CreateOrganizationRemoteSessionClientForm$outboundSchema.parse(
      createOrganizationRemoteSessionClientForm,
    ),
  );
}
